import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { BsEye, BsPlusCircle, BsSortDown } from 'react-icons/bs';
import { connect, ConnectedProps } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { DEFAULTS } from '../../../common';
import { FixedDotSpinner } from '../../../components/DotSpinner';
import FetchError from '../../../components/FetchError';
import PageTitle from '../../../components/PageTitle';
import SearchPagination from '../../../components/Pagination';
import TextIcon from '../../../components/textIcon';
import { useDebounce } from '../../../hooks';
import { ISourceBase } from '../../../interfaces/sources';
import { moveControlToScreenCenter } from '../../../scrolls';
import { RootState } from '../../../store';
import { getControlData } from '../../../utils';
import { useSourceListQuery } from '../../services/sources';
import { changeNotRankedOnly, changeSourceOrder, changeSourcePageNo, changeSourceTerm } from '../manageSlice';
import OrderButton from '../OrderButton';
import SourceDialog from './SourceDialog';
import SourceItem from './SourceItem';

interface ISourcesManageLayoutProps extends PropsFromRedux {}
const SourcesManageLayout = ({
	term,
	selectedOrderField,
	selectedOrderAsc,
	pageNo,
	notRankedOnly,
	changeSourcePageNo,
	changeSourceOrder,
	changeSourceTerm,
	changeNotRankedOnly,
}: ISourcesManageLayoutProps) => {
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const { term: visibleTerm, onTermChange, debouncedValue: debouncedTerm } = useDebounce(term);
	const { data, error, isFetching } = useSourceListQuery(
		{ term, order: selectedOrderField, pageNo, extra: { notRankedOnly, asc: selectedOrderAsc } },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	useEffect(() => {
		changeSourceTerm(debouncedTerm);
	}, [debouncedTerm, changeSourceTerm]);

	const onEditButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		const button = (event.target as HTMLElement).closest('.btn-edit') as HTMLButtonElement;
		if (!button) return;
		const { sourceId } = button.dataset;
		if (sourceId === undefined) return;
		event.stopPropagation();
		setSelectedId(parseInt(sourceId));
	}, []);

	const onCloseDialog = useCallback((sourceId?: number) => {
		setSelectedId(null);
		if (!sourceId) return;
		setTimeout(() => {
			moveControlToScreenCenter(document.getElementById(`source_${sourceId}`), true);
		}, 100);
	}, []);

	const onOrderClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			const order = getControlData(event.target, '.btn', 'order') as keyof ISourceBase;
			if (!order) return;
			changeSourceOrder(order);
		},
		[changeSourceOrder]
	);

	const onChangeNotRankedClick = () => changeNotRankedOnly(!notRankedOnly);

	const filtered = debouncedTerm.includes('.');

	return (
		<>
			<PageTitle title="Джерела" />
			<Row>
				<Col className="hstack gap-1 align-items-center">
					<h3 className="m-0">
						Кількість знайдених джерел —<span className="ms-1 fw-bold text-info">{data?.total}</span>
					</h3>
					<Button className="ms-auto btn-edit" data-source-id={0} onClick={onEditButtonClick}>
						<TextIcon Icon={BsPlusCircle} size="lg">
							Додати
						</TextIcon>
					</Button>
				</Col>
			</Row>
			<Row className="mt-3 align-items-center">
				<Col>
					<Form.Control
						type="text"
						placeholder="введіть URL джерела для фільтрації"
						title="URL джерела для фільтрації"
						value={visibleTerm}
						onChange={onTermChange}
						// disabled={isFetching}
					/>
				</Col>
				<Col xs="auto">
					<Form.Check
						type="switch"
						id="cbNotRankedOnly"
						label="Тільки неоцінені джерела"
						checked={notRankedOnly}
						onChange={onChangeNotRankedClick}
						disabled={isFetching}
					/>
				</Col>
			</Row>
			<Row className="mt-1">
				{error && (
					<Col xs={12}>
						<FetchError error={error} />
					</Col>
				)}
				<Col className="position-relative">
					<Table striped onClick={onEditButtonClick} responsive>
						<thead>
							<tr>
								<th className="text-nowrap">
									ID
									{!filtered && (
										<OrderButton
											orderField="id"
											selectedOrderField={selectedOrderField}
											selectedOrderAsc={selectedOrderAsc}
											onOrderClick={onOrderClick}
										/>
									)}
								</th>
								<th title="Чи може бути знайдене користувачем та приєднане до документу">
									<BsEye className="mt-n1" />
								</th>
								<th className="text-nowrap">
									Назва
									{!filtered && (
										<OrderButton
											orderField="title"
											selectedOrderField={selectedOrderField}
											selectedOrderAsc={selectedOrderAsc}
											onOrderClick={onOrderClick}
										/>
									)}
								</th>
								<th>Домен{filtered && <BsSortDown className="icon-lg ms-2 text-primary" />}</th>
								<th>Країна</th>
								<th>Якість</th>
								<th className="text-nowrap">
									Оновлено
									{!filtered && (
										<OrderButton
											orderField="update_date"
											selectedOrderField={selectedOrderField}
											selectedOrderAsc={selectedOrderAsc}
											onOrderClick={onOrderClick}
										/>
									)}
								</th>
								<th>Дії</th>
							</tr>
						</thead>
						{data && (
							<tbody>
								{data.sources.map((source) => (
									<SourceItem key={source.id} source={source} />
								))}
								<tr className="show-only-child">
									<td colSpan={7}>&empty; Відсутні записи, що задовольняють фільтру</td>
								</tr>
							</tbody>
						)}
					</Table>
					{isFetching && <FixedDotSpinner>Завантаження списку джерел</FixedDotSpinner>}
				</Col>
			</Row>
			{data && (
				<Row>
					<Col>
						<SearchPagination
							pageCount={Math.ceil(data.total / DEFAULTS.sourcesPerPage)}
							activePageNo={data.pageNo}
							onPageClick={changeSourcePageNo}
						/>
					</Col>
				</Row>
			)}
			{selectedId !== null && (
				<SourceDialog source={data?.sources.find(({ id }) => id === selectedId)} onClose={onCloseDialog} />
			)}
			<Outlet />
		</>
	);
};

const mapState = (state: RootState) => ({
	term: state.manage.sourceTerm,
	selectedOrderField: state.manage.sourceOrderField,
	selectedOrderAsc: state.manage.sourceOrderAsk,
	pageNo: state.manage.sourcePageNo,
	notRankedOnly: state.manage.sourceNotRankedOnly,
});

const mapDispatch = { changeSourcePageNo, changeSourceOrder, changeSourceTerm, changeNotRankedOnly };

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SourcesManageLayout);
